/**
 * CollectionOverviewPage - Gateway page between Collections Home and Records View
 *
 * Atomic level: Page
 * Shows metric strip (24h deltas), queue tabs (top 10), and report tiles.
 */

import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useCollection, useRecords } from '../../../hooks/useRecords';
import { useCollectionMetrics } from './hooks/useCollectionMetrics';
import { MonitoringDot } from './components/MonitoringDot';
import { DetectionPills } from './components/DetectionPills';
import { QueueTabs } from './components/QueueTabs';
import { CollectionMetricStrip } from './components/CollectionMetricStrip';
import { QueuePreviewTable } from './components/QueuePreviewTable';
import { CauseReportTile } from './components/CauseReportTile';
import { OutcomesReportTile } from './components/OutcomesReportTile';
import { TcfReportTile } from './components/TcfReportTile';
import type { QueueType } from './components/QueueTabs';
import type { RecordFilters } from '../../../services/types/collection';

const formatRelativeTime = (dateString: string | null): string => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
};

const getQueueFilters = (queue: QueueType): RecordFilters => {
  const base: RecordFilters = { sortBy: 'conduct_score', sortOrder: 'desc' };
  switch (queue) {
    case 'complaints':
      return { ...base, regulatoryFlags: ['complaint'] };
    case 'vulnerability':
      return { ...base, regulatoryFlags: ['vulnerability'] };
    case 'all':
    default:
      return base;
  }
};

const CollectionOverviewPage: React.FC = () => {
  const { collectionId } = useParams<{ collectionId: string }>();
  const [activeQueue, setActiveQueue] = useState<QueueType>('complaints');

  // Data fetching
  const {
    data: collectionData,
    isLoading: collectionLoading,
    error: collectionError,
  } = useCollection(collectionId!);
  const { data: metricsData } = useCollectionMetrics(collectionId!);

  // Queue data — top 10
  const queueFilters = getQueueFilters(activeQueue);
  const { data: queueData } = useRecords(collectionId!, queueFilters, 1, 10);

  // Queue counts come straight from the collection record (populated server-side
  // on the list/detail endpoints), so no extra round-trips per tab.

  if (collectionLoading) {
    return (
      <div className="flex flex-col gap-6 p-4 sm:p-6 w-full animate-pulse">
        <div className="h-4 w-48 bg-muted rounded" />
        <div className="h-8 w-64 bg-muted rounded" />
        <div className="grid grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-28 bg-muted rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-muted rounded-xl" />
      </div>
    );
  }

  const collection = collectionData?.collection;
  const stats = collectionData?.stats;
  if (!collection) {
    const notFound = (collectionError as any)?.status === 404;
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6 w-full">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Link to="/v4/records" className="text-primary font-medium hover:underline">
            Monitor
          </Link>
          <span>/</span>
          <span>{notFound ? 'Not found' : 'Error'}</span>
        </div>
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-border/70 bg-card p-10 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">
            {notFound ? 'Collection not found' : 'Failed to load collection'}
          </h2>
          <p className="max-w-md text-sm text-muted-foreground">
            {notFound
              ? "This collection doesn't exist or you don't have access to it."
              : collectionError?.message ?? 'Something went wrong. Please try again.'}
          </p>
          <Link
            to="/v4/records"
            className="text-sm font-medium text-primary hover:underline"
          >
            ← Back to Monitor
          </Link>
        </div>
      </div>
    );
  }

  const complaintsCount = collection.eligibleComplaintsCount ?? 0;
  const vulnerableCount = collection.vulnerableCustomersCount ?? 0;
  const allCount = collection.recordCount ?? 0;

  // Root causes from aggregate stats (if available)
  const rootCauses = queueData?.aggregateStats
    ? (queueData.aggregateStats.rootCausesByCategory || []).map((item) => ({
        name: item.label,
        percentage: item.value,
      }))
    : [];
  const topRootCauseLabels = queueData?.aggregateStats?.topRootCauseLabels ?? [];

  // Consumer Duty outcomes (from stats if available)
  const outcomes = [
    { name: 'Consumer Support', score: stats?.avgConductScore ?? 0 },
    { name: 'Price & Fair Value', score: (stats?.dissatisfactionRatio ?? 0) / 10 },
    { name: 'Consumer Understanding', score: (stats?.vulnerabilityExposure ?? 0) / 10 },
    { name: 'Product Design', score: (stats?.eligibleComplaintsRatio ?? 0) / 10 },
  ];

  return (
    <div className="flex h-full min-h-0 flex-col gap-5 overflow-y-auto p-4 sm:p-6 w-full">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Link to="/v4/records" className="text-primary font-medium hover:underline">
          Monitor
        </Link>
        <span>/</span>
        <span>{collection.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <MonitoringDot status={collection.monitoringStatus ?? 'active'} />
            <h1 className="text-[22px] font-extrabold tracking-tight">{collection.name}</h1>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {(() => {
              const source = collection.source;
              // Display maps duplicated from CollectionCard — small + stable,
              // no util file to import. If a third surface needs the same
              // maps, lift them into a shared module.
              const skillNames: Record<string, string> = {
                'intercom-reporting': 'Intercom',
                'dataeq-analyse': 'DataEQ',
              };
              const cadenceLabels: Record<string, string> = {
                every_30m: 'Every 30 min',
                every_1h: 'Every hour',
                every_3h: 'Every 3 hours',
                every_6h: 'Every 6 hours',
                every_12h: 'Every 12 hours',
                every_24h: 'Daily',
                daily: 'Daily',
                weekly: 'Weekly',
                monthly: 'Monthly',
              };
              const sourcePart = source
                ? `${skillNames[source.skillName] || source.skillName} · ${cadenceLabels[source.cadence] || source.cadence}`
                : collection.type;
              // When a source is attached but its first tick hasn't run
              // yet, lastRunAt is null — fall back to dataset.updatedAt
              // ONLY for manual collections, otherwise show "Never" so the
              // header doesn't lie about a sync that never happened.
              let lastSync: string;
              if (source) {
                lastSync = source.lastRunAt
                  ? formatRelativeTime(source.lastRunAt)
                  : 'Never';
              } else {
                lastSync = formatRelativeTime(collection.updatedAt);
              }
              return `${sourcePart} · Last synced ${lastSync} · ${(collection.recordCount ?? 0).toLocaleString()} records`;
            })()}
          </p>
        </div>
        <DetectionPills showCounts={false} />
      </div>

      {/* Metric Strip */}
      {metricsData && (
        <CollectionMetricStrip metrics={metricsData.metrics} />
      )}

      {/* Queue Tabs + Table */}
      <div>
        <QueueTabs
          eligibleComplaintsCount={complaintsCount}
          vulnerableCustomersCount={vulnerableCount}
          allRecordsCount={allCount}
          activeQueue={activeQueue}
          onTabChange={setActiveQueue}
        />

        {queueData && (
          <QueuePreviewTable
            records={queueData.records}
            collectionId={collectionId!}
            activeQueue={activeQueue}
            totalCount={
              activeQueue === 'complaints'
                ? complaintsCount
                : activeQueue === 'vulnerability'
                ? vulnerableCount
                : allCount
            }
          />
        )}
      </div>

      {/* Report Tiles */}
      <div>
        <h2 className="text-[13px] font-bold uppercase tracking-wider text-foreground mb-3">
          Reports
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
          <CauseReportTile
            collectionId={collectionId!}
            rootCauses={rootCauses}
            topLabels={topRootCauseLabels}
          />
          <OutcomesReportTile
            collectionId={collectionId!}
            outcomes={outcomes}
          />
          <TcfReportTile collectionId={collectionId!} />
        </div>
      </div>
    </div>
  );
};

export { CollectionOverviewPage };
export default CollectionOverviewPage;
